import type { ReactNode, Ref, TableHTMLAttributes } from 'react';

import type { Spacing } from '@shieldpay/theme-provider-ui/theme/types';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import { Box } from '../box/box';
import { Card } from '../card/card';
import { ScreenReaderText } from '../screen-reader-text/screen-reader-text';

import { TableBody } from './table-body';
import { TableActionCell, TableDataCell } from './table-data-cell';
import { TableHead } from './table-head';
import { TableHeaderCell } from './table-header-cell';
import { TableRow } from './table-row';

import * as styles from './table.css';

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  /**
   * Additional classes to apply.
   */
  className?: string;
  /**
   * Child nodes (e.g. TableHead, TableBody).
   */
  children: ReactNode;
  /**
   * Table heading. Visually hidden but accessible to screen readers.
   */
  caption: ReactNode;
  /**
   * Borderless changes whether the component uses a box or a card as a container.
   */
  variant?: 'bordered' | 'borderless';
  'data-testid'?: string;
  ref?: Ref<HTMLDivElement>;
  minHeight?: Spacing;
}

const TEST_IDS = {
  TABLE: 'table-component',
};

export const Table = ({
  children,
  caption,
  'data-testid': testId = TEST_IDS.TABLE,
  className,
  variant = 'bordered',
  ref,
  minHeight,
  style,
}: TableProps) => {
  const Component = variant === 'bordered' ? Card : Box;

  return (
    <Component
      padding="none"
      data-testid={testId}
      ref={ref}
      size={{ minHeight }}
      style={style}
    >
      <table
        className={classFilter([
          styles.table,
          variant === 'bordered' && styles.tableHasBorder,
          className,
        ])}
      >
        <ScreenReaderText Component="caption">{caption}</ScreenReaderText>
        {children}
      </table>
    </Component>
  );
};

Table.Body = TableBody;
Table.Head = TableHead;
Table.HeaderCell = TableHeaderCell;
Table.DataCell = TableDataCell;
Table.ActionCell = TableActionCell;
Table.Row = TableRow;
